import React, { useEffect, useState } from "react";
import { Button, Icon } from "@blueprintjs/core";
import compensate2 from "./assets/compensate2.png";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { CrossoverScreenResult, CrossoverScreenInitialResult } from "./CrossoverResultTypes";
import { useLanguage } from "../../../../context/LanguageContext";
import { DefaultOption } from "../../../../translations/experiments/Default";

function CompensationBanner({ selected, setSelected, isDefault, t }: {
    selected: number,
    setSelected: (idx: number) => void,
    isDefault: (idx: number) => boolean,
    t: any
}) {
    const options = t.compensationBanner.options;
    return (
        <div style={{
            maxWidth: 700,
            minWidth: 340,
            margin: "40px auto 0 auto",
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 32px 0 rgba(0,0,0,0.10)",
            padding: "20px 28px 40px 28px",
            position: "relative",
            height: 230,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start"
        }}>
            <div style={{ position: "absolute", left: 20, top: 20 }}>
                <Icon icon={t.compensationBanner.icon} color="#137cbd" style={{ fontSize: 28 }} />
            </div>
            <div style={{ textAlign: "center", marginBottom: 10 }}>
                <span style={{
                    fontWeight: 600,
                    fontSize: 17,
                    letterSpacing: "0.01em",
                    display: "inline-block",
                    marginBottom: 5
                }}>
                    {t.compensationBanner.title}
                </span>
                <div style={{
                    fontSize: 15,
                    color: "#444",
                    margin: "0 auto",
                    maxWidth: 650,
                    textAlign: "justify",
                    lineHeight: 1.45,
                    marginBottom: 0
                }}>
                    {t.compensationBanner.description}
                </div>
            </div>
            <div style={{
                display: 'flex',
                gap: '18px',
                justifyContent: 'center',
                marginTop: 14,
                marginBottom: 3
            }}>
                {options.map((opt: any, idx: number) => (
                    <Button
                        key={idx}
                        large
                        fill
                        outlined={selected !== idx}
                        intent={selected === idx ? "primary" : "none"}
                        style={{
                            minWidth: 220,
                            maxWidth: 300,
                            border: selected === idx ? "2.5px solid #137cbd" : "1px solid #bbb",
                            background: selected === idx ? "#137cbd" : "#fff",
                            fontWeight: 500,
                            boxShadow: selected === idx ? "0 0 10px #137cbd44" : undefined,
                            transition: "border 0.2s, box-shadow 0.2s",
                            color: selected === idx ? "#fff" : "#222",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative"
                        }}
                        onClick={() => setSelected(idx)}
                        tabIndex={idx + 1}
                    >
                        {selected === idx && (
                            <Icon
                                icon="tick"
                                color="#fff"
                                style={{
                                    position: "absolute",
                                    top: 10,
                                    right: 10,
                                    fontSize: 20,
                                    background: "#137cbd",
                                    borderRadius: "50%",
                                    padding: 3,
                                    boxShadow: "0 0 4px #137cbd66"
                                }}
                            />
                        )}
                        <span>
                            {opt.label}
                            {opt.hint && (
                                <span style={{
                                    display: "block",
                                    fontSize: 13,
                                    color: selected === idx ? "#fff" : "#137cbd",
                                    fontWeight: 400,
                                    marginTop: 4
                                }}>
                                    {opt.hint}
                                </span>
                            )}
                        </span>
                    </Button>
                ))}
            </div>
        </div>
    );
}

export function CO2ScreenCompDefault(props: ScreenProps) {
    const defaultOption = 0;
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [selected, setSelected] = useState<number>(defaultOption);
    const { setCanContinue, setResult } = props;
    const { language } = useLanguage();
    const t = DefaultOption[language].screens.screen4;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
        setCanContinue(true);
        setResult(() => ({ ...CrossoverScreenInitialResult }));
    }, [setCanContinue, setResult]);

    useEffect(() => {
        setCanContinue(true);
        setResult((result: CrossoverScreenResult) => {
            result.selections = [];
            result.selections.push({
                choice: selected,
                millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp,
                defaultOption
            });
            return result;
        });
    }, [selected, setCanContinue, setResult, firstRenderTimestamp, defaultOption]);

    const isDefault = (idx: number) => idx === defaultOption;

    return (
        <div className="experiment-container">
            <div style={{
                marginBottom: 10,
                fontSize: 17,
                lineHeight: 1.7
            }}>
                {t.intro.map((p: string, i: number) => (
                    <p key={i} style={{ margin: i === 0 ? "0 0 10px 0" : "10px 0 0 0" }}>{p}</p>
                ))}
            </div>
            <div style={{ textAlign: "center", marginBottom: 18 }}>
                <img
                    src={compensate2}
                    alt={t.compensationImgAlt}
                    style={{
                        maxWidth: 420,
                        width: "90%",
                        height: "auto",
                        marginBottom: 18,
                        borderRadius: 12,
                        boxShadow: "0 2px 18px #137cbd33"
                    }}
                />
            </div>
            <div style={{
                marginBottom: 18,
                fontSize: 17,
                lineHeight: 1.7
            }}>
                <p>
                    <b>{t.beforeBookingNotice}</b>
                </p>
            </div>
            <CompensationBanner
                selected={selected}
                setSelected={setSelected}
                isDefault={isDefault}
                t={t}
            />
        </div>
    );
}

export const CrossoverScreen4Config: ScreenConfig = {
    component: CO2ScreenCompDefault,
};